import { isAfterDate, isBeforeDate } from '@tickitacka/shared';

/**
 * Mantine DatePicker 컴포넌트의 renderDay 함수에서 사용할 날짜 스타일링 유틸리티
 * Blocknote와의 스타일 충돌을 방지하기 위해 명시적인 인라인 스타일 사용
 *
 * @param date 스타일을 적용할 날짜
 * @param selectedDate 현재 선택된 날짜
 * @returns 스타일 객체
 */
export const getDateCellStyle = (
    date: Date,
    selectedDate: Date | null
): React.CSSProperties => {
    const day = date.getDay();
    const isWeekend = day === 0 || day === 6; // 0: 일요일, 6: 토요일

    // 오늘 날짜 확인 (시간 제외하고 날짜만 비교)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateToCheck = new Date(date);
    dateToCheck.setHours(0, 0, 0, 0);

    // 날짜 비교
    const isToday =
        !isBeforeDate(dateToCheck, today) && !isAfterDate(dateToCheck, today);
    const isSelected =
        selectedDate && date.toDateString() === selectedDate.toDateString();

    // 기본 스타일 설정
    const styleObj: React.CSSProperties = {
        fontSize: 'calc(var(--day-size) / 2.8)',
    };

    // 주말은 빨간색
    if (isWeekend) {
        styleObj.color = 'var(--mantine-color-red-6)';
    }

    // 오늘 날짜 강조
    if (isToday) {
        styleObj.fontWeight = 'bold';
    }

    // 선택된 날짜 강조
    if (isSelected) {
        styleObj.width = 'var(--day-size, var(--day-size-sm))';
        styleObj.height = 'var(--day-size, var(--day-size-sm))';
        styleObj.display = 'inline-flex';
        styleObj.justifyContent = 'center';
        styleObj.alignItems = 'center';
        styleObj.borderRadius = 'var(--mantine-radius-default)';
        styleObj.backgroundColor = 'var(--mantine-primary-color-filled)';
        styleObj.color = 'var(--mantine-primary-color-contrast)';
    }

    return styleObj;
};

/**
 * Mantine DatePicker 컴포넌트의 renderDay 함수 생성 유틸리티
 *
 * @param selectedDate 선택된 날짜
 * @param options 추가 옵션 (미래 확장성 위해)
 * @returns renderDay 함수
 */
export const createRenderDayFunction = (
    selectedDate: Date,
    options?: {
        disableFutureDates?: boolean;
        // highlightWeekends?: boolean;
    }
) => {
    const renderDay = (date: Date) => {
        const styleObj = getDateCellStyle(date, selectedDate);

        // 미래 날짜 비활성화 옵션
        if (options?.disableFutureDates) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (date > today) {
                styleObj.opacity = 0.5;
                styleObj.cursor = 'not-allowed';
            }
        }

        return <div style={styleObj}>{date.getDate()}</div>;
    };

    return renderDay;
};

/**
 * 날짜 범위 선택에 필요한 getDayProps 함수 생성 유틸리티
 *
 * @param tempRange 현재 선택된 날짜 범위 [시작일, 종료일]
 * @param hoveredDate 호버된 날짜 (선택 중 미리보기용)
 * @param setHoveredDate 호버된 날짜 설정 함수
 * @param baseDayProps 기본 DayProps
 * @param options 추가 옵션
 * @returns DatePickerInput 컴포넌트의 getDayProps 함수
 */
export const createDateRangeGetDayProps = (
    tempRange: [Date | null, Date | null],
    hoveredDate: Date | null,
    setHoveredDate: (date: Date | null) => void,
    baseDayProps?: ((date: Date) => any) | null,
    options?: {
        rangeHoverColor?: string;
        rangeSelectedColor?: string;
        rangeBoundaryColor?: string;
    }
): ((date: Date) => {
    disabled?: boolean;
    style?: React.CSSProperties;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
}) => {
    // 옵션 기본값 설정
    const {
        rangeHoverColor = 'var(--mantine-color-blue-0)',
        rangeSelectedColor = 'var(--mantine-color-blue-1)',
        rangeBoundaryColor = 'var(--mantine-primary-color-filled)',
    } = options || {};

    return (date: Date) => {
        // 기본 props 가져오기 (있는 경우)
        const baseProps = baseDayProps ? baseDayProps(date) : {};

        // 마우스 이벤트 처리
        const events = {
            onMouseEnter: () => setHoveredDate(date),
            onMouseLeave: () => setHoveredDate(null),
        };

        // 날짜 범위 호버 효과를 위한 스타일
        const rangeStyle: React.CSSProperties = {};
        const currentDate = new Date(date);
        currentDate.setHours(0, 0, 0, 0);

        // 주말 여부 확인
        const day = currentDate.getDay();
        const isWeekend = day === 0 || day === 6; // 0: 일요일, 6: 토요일

        // 이미 선택된 범위 표시
        if (tempRange[0] && tempRange[1]) {
            const startDate = new Date(tempRange[0]);
            const endDate = new Date(tempRange[1]);

            startDate.setHours(0, 0, 0, 0);
            endDate.setHours(0, 0, 0, 0);

            // 범위 내의 날짜인지 확인
            const isInRange =
                currentDate >= startDate && currentDate <= endDate;

            // 시작 또는 끝 날짜인지 확인
            const isStartDate = currentDate.getTime() === startDate.getTime();
            const isEndDate = currentDate.getTime() === endDate.getTime();
            const isRangeBoundary = isStartDate || isEndDate;

            // 주말 날짜는 빨간색 텍스트로
            if (isWeekend) {
                rangeStyle.color = 'var(--mantine-color-red-6)';
            }

            // 범위 내 날짜 스타일링
            if (isInRange && !isRangeBoundary) {
                // 범위 내 기본 스타일
                rangeStyle.backgroundColor = rangeSelectedColor;
            }

            // 범위 시작/종료 날짜 스타일링 (주말 여부와 관계없이)
            if (isRangeBoundary) {
                // 범위 경계 날짜는 항상 흰색 텍스트로
                rangeStyle.backgroundColor = rangeBoundaryColor;
                rangeStyle.color = 'var(--mantine-primary-color-contrast)'; // 항상 흰색(대비색)
                rangeStyle.fontWeight = 'bold';
            }
        }
        // 첫 번째 날짜만 선택된 상태
        else if (tempRange[0] && !tempRange[1]) {
            const startDate = new Date(tempRange[0]);
            startDate.setHours(0, 0, 0, 0);

            // 시작 날짜인지 확인
            const isStartDate = currentDate.getTime() === startDate.getTime();

            // 시작 날짜는 진한 파란색 배경과 흰색 텍스트
            if (isStartDate) {
                // 범위 경계 날짜는 항상 흰색 텍스트로
                rangeStyle.backgroundColor = rangeBoundaryColor;
                rangeStyle.color = 'var(--mantine-primary-color-contrast)'; // 항상 흰색(대비색)
                rangeStyle.fontWeight = 'bold';
            } else if (isWeekend) {
                // 시작 날짜가 아닌 주말 날짜는 빨간색 텍스트로
                rangeStyle.color = 'var(--mantine-color-red-6)';
            }

            // 호버 효과 처리 (호버된 날짜가 있는 경우)
            if (hoveredDate) {
                const hoverDate = new Date(hoveredDate);

                startDate.setHours(0, 0, 0, 0);
                hoverDate.setHours(0, 0, 0, 0);

                // 시작 날짜와 호버 날짜의 순서 확인
                const isStartBeforeHover = !isAfterDate(startDate, hoverDate);
                const minDate = isStartBeforeHover ? startDate : hoverDate;
                const maxDate = isStartBeforeHover ? hoverDate : startDate;

                // 현재 날짜가 범위에 있는지 확인
                const isInRange =
                    !isBeforeDate(currentDate, minDate) &&
                    !isAfterDate(currentDate, maxDate);

                // 시작 또는 호버 날짜인지 확인
                const isStartDate =
                    currentDate.getTime() === startDate.getTime();
                const isHoverDate =
                    currentDate.getTime() === hoverDate.getTime();
                const isRangeBoundary = isStartDate || isHoverDate;

                // 호버 범위 내 날짜
                if (isInRange && !isRangeBoundary) {
                    rangeStyle.backgroundColor = rangeHoverColor;

                    // 주말 날짜는 빨간색 유지 (범위 내 주말)
                    if (isWeekend) {
                        rangeStyle.color = 'var(--mantine-color-red-6)';
                    }
                }

                // 시작 날짜와 호버 날짜는 진한 파란색 배경과 흰색 텍스트
                if (isRangeBoundary) {
                    // 범위 경계 날짜는 항상 흰색 텍스트로
                    rangeStyle.backgroundColor = rangeBoundaryColor;
                    rangeStyle.color = 'var(--mantine-primary-color-contrast)'; // 항상 흰색(대비색)
                    rangeStyle.fontWeight = 'bold';

                    // 주말 스타일은 여기서 적용하지 않음 (항상 흰색 유지)
                }
            }
        } else if (isWeekend) {
            // 아무것도 선택되지 않은 상태에서는 주말 날짜만 빨간색으로
            rangeStyle.color = 'var(--mantine-color-red-6)';
        }

        return {
            ...baseProps,
            ...events, // 마우스 이벤트 포함
            style: {
                ...baseProps.style,
                ...rangeStyle,
            },
        };
    };
};

/**
 * DateRange용 renderDay 함수 생성 유틸리티
 *
 * @param options 날짜 렌더링 옵션
 * @returns DatePickerInput 컴포넌트의 renderDay 함수
 */
export const createDateRangeRenderDay = (options?: {
    highlightToday?: boolean;
    todayColor?: string;
    weekendColor?: string;
    disableFutureDates?: boolean;
}) => {
    // 옵션 기본값 설정
    const {
        highlightToday = true,
        todayColor = 'var(--mantine-primary-color-filled)',
        disableFutureDates = false,
    } = options || {};

    const renderDay = (date: Date) => {
        // 오늘 날짜와 과거/미래 여부 확인
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dateToCheck = new Date(date);
        dateToCheck.setHours(0, 0, 0, 0);

        const isPast = dateToCheck < today;
        const isToday = dateToCheck.getTime() === today.getTime();
        const isFuture = dateToCheck > today;

        // 스타일 설정
        const styleObj: React.CSSProperties = {
            fontSize: 'calc(var(--day-size) / 2.8)',
        };

        // 오늘 날짜 강조
        if (highlightToday && isToday) {
            styleObj.width = 'var(--day-size, var(--day-size-sm))';
            styleObj.height = 'var(--day-size, var(--day-size-sm))';
            styleObj.display = 'inline-flex';
            styleObj.justifyContent = 'center';
            styleObj.alignItems = 'center';
            styleObj.borderRadius = 'var(--mantine-radius-default)';
            styleObj.backgroundColor = todayColor;
            styleObj.color = 'var(--mantine-primary-color-contrast)';
        }

        // 미래 날짜 비활성화 옵션
        if (disableFutureDates && isFuture) {
            styleObj.opacity = '0.5';
            styleObj.cursor = 'not-allowed';
        }

        return <div style={styleObj}>{date.getDate()}</div>;
    };

    return renderDay;
};
